// Build marker: re-deploy 2026-06-09 to refresh the prod bundle (no functional
// change) — Requests sub-tab bar white + filter-row stability already in master.
import { useState } from "react";
import ViewModeToggle from "../common/ViewModeToggle";

const TenderTabs = ({
  activeTab,
  onTabChange,
  isBgWhite = true,
  isBorder = true,
  isFromAgent = false,
  isFromClub = false,
  isFromStaff = false,
  hideTenderTabs = false,
  onPositionFilterChange,
  activePositionFilter: externalActivePositionFilter,
  // Cards/List toggle now lives on this category-tab row (right end) instead
  // of the crowded filter toolbar. Rendered only when a caller wires it, so
  // dashboards that don't use the toggle are unaffected.
  viewMode,
  onViewModeChange,
  // Optional node rendered at the row START (the club requests page slots the
  // lifecycle segment in here from 1440px up, so segment and category pills
  // share one line). Left out -> the row renders exactly as before.
  leftSlot = null,
  // Staff/coach WITHOUT a club browse as jobseekers: they can only ever
  // apply to staff-category requests (BE CreatePitchViewSet.perform_create —
  // player tenders demand the Agent group, staff tenders the Staff group),
  // so the "Players" pill is not offered to them at all instead of leading
  // to cards they can do nothing with. Coaches/Staffs/Others all map onto
  // tender_category='staff' (BE _filter_by_tender_category) and stay.
  // In-club staff keep the full pill row unchanged.
  jobseekerCategories = false,
}) => {
  const [internalActivePositionFilter, setInternalActivePositionFilter] = useState("players");
  
  // Use external activePositionFilter if provided, otherwise use internal state
  const activePositionFilter = externalActivePositionFilter !== undefined ? externalActivePositionFilter : internalActivePositionFilter;
  
  const tabs = [
    { id: "tp-tenders", label: "TP Requests" },
    { id: "tp-lite-tenders", label: "TP Lite Requests" },
  ];

  // Filter position options based on user role
  // Agent role: Players, Coaches and Staffs
  //   (Coaches are staff tenders whose position is a coaching role — the
  //    backend list/filters endpoints already map tender_category=coaches to
  //    tender_category='staff' + position icontains 'coach', so no backend
  //    change is needed to expose this tab.)
  // Club/Staff role: all 4 options
  const positionFilters = jobseekerCategories
    ? [
        { id: "coaches", label: "Coaches" },
        { id: "staffs", label: "Staffs" },
        { id: "others", label: "Others" },
      ]
    : isFromAgent
    ? [
        { id: "players", label: "Players" },
        { id: "coaches", label: "Coaches" },
        { id: "staffs", label: "Staffs" },
      ]
    : [
        { id: "players", label: "Players" },
        { id: "coaches", label: "Coaches" },
        { id: "staffs", label: "Staffs" },
        { id: "others", label: "Others" },
      ];

  const handlePositionFilterChange = (filterId) => {
    // Ensure we never clear to "all" — default to the first offered pill
    // (jobseekers have no "players" pill to fall back to).
    const safeFilterId = filterId || positionFilters[0].id;
    setInternalActivePositionFilter(safeFilterId);
    if (onPositionFilterChange) {
      onPositionFilterChange(safeFilterId);
    }
  };

  // With a leftSlot the row is two-sided even when the TP/TP Lite switch is
  // hidden: slot left, category pills + toggle right.
  const containerJustify =
    hideTenderTabs && !leftSlot ? "justify-end" : "justify-between";

  return (
    <div
      className={`flex flex-wrap ${containerJustify} items-center gap-3 mb-6 px-4 md:px-12 ${
        isBgWhite ? "bg-white" : "bg-[#F8F9FB]"
      } py-4 ${isBorder ? "border-b border-[#E5E6E8]" : ""}`}
    >
      {leftSlot}
      {!hideTenderTabs && (
        <div className="w-fit border border-[#E5E6E8] bg-[#F8F9FB] p-1 rounded-lg">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`px-4 py-2 text-sm rounded-lg ${
                activeTab === tab.id
                  ? "bg-white text-black"
                  : "text-[#1E1E1ECC] hover:text-gray-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {(isFromAgent || isFromClub || isFromStaff) && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {positionFilters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => handlePositionFilterChange(filter.id)}
                className={`px-4 py-2 rounded-full text-sm font-normal ${
                  activePositionFilter === filter.id
                    ? "border border-[#2283FF] text-[#2283FF] bg-[#E9F3FF]"
                    : "border border-[#E6E6E6] text-[#1E1E1ECC]"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Cards/List toggle — right end of the category-tab row. */}
          {onViewModeChange && (
            <ViewModeToggle value={viewMode} onChange={onViewModeChange} />
          )}
        </div>
      )}
    </div>
  );
};

export default TenderTabs;
